import React from "react";
import { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { generateChartColors } from "visual-components/analytics/campaign/chartColors";

/**
 * Single bar item component
 * @param {Object} props
 * @param {string} props.label - The label text
 * @param {number} props.value - The numeric value
 * @param {number} [props.percentage] - The percentage value
 * @param {boolean} [props.showPercentage=true] - Whether to show percentage
 * @param {number} props.widthPercent - Bar width as percentage of max value
 * @param {string} props.color - Bar color
 * @param {Object} props.item - The full data item for custom tooltip rendering
 * @param {number} [props.threshold] - Threshold value to display as a line on the bar
 * @param {number} [props.thresholdPercent] - Threshold position as percentage of max value
 * @param {Function} [props.tooltip] - Custom tooltip renderer: ({label, value, percentage, item}) => ReactNode
 * @returns {import("react").ReactElement}
 */
function BarItem(props) {
  const {
    label,
    value,
    percentage,
    showPercentage = true,
    widthPercent,
    color,
    item,
    threshold,
    thresholdPercent,
    tooltip,
  } = props;

  // Scale bar width (use full container width since flex handles the layout)
  const scaledWidth = Math.max(widthPercent, 2);
  const scaledThreshold = thresholdPercent ? Math.min(thresholdPercent, 100) : 0;

  const tooltipContent = tooltip ? tooltip({ label, value, percentage, item }) : null;

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={200}
      slotProps={{ tooltip: { sx: { fontSize: "1.2rem" } } }}
    >
      <Box sx={{ mb: 1, cursor: tooltip ? "pointer" : "default" }}>
        {/* Row 1: Label on left, Number on right */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 12,
              color: "#333",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "70%",
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: 12,
              color: "#333",
              fontWeight: 500,
            }}
          >
            {value?.toLocaleString()}
          </Typography>
        </Box>

        {/* Row 2: Bar on left, Percentage on right (optional) */}
        <Box sx={{ display: "flex", alignItems: "center", mt: 0.25, gap: 1 }}>
          {/* Bar container with optional threshold line */}
          <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
            {/* Gray threshold background (from end of bar to threshold line) */}
            {threshold > 0 && scaledThreshold > scaledWidth && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: `${scaledWidth}%`,
                  height: 14,
                  width: `${scaledThreshold - scaledWidth}%`,
                  backgroundColor: "#e0e0e0",
                }}
              />
            )}
            {/* Main bar */}
            <Box
              sx={{
                position: "relative",
                height: 14,
                backgroundColor: color,
                borderRadius: 0,
                width: `${scaledWidth}%`,
              }}
            />
            {/* Threshold line */}
            {threshold > 0 && scaledThreshold > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: -2,
                  left: `${scaledThreshold}%`,
                  width: 2,
                  height: 18,
                  backgroundColor: "#666",
                }}
              />
            )}
          </Box>
          {showPercentage && percentage !== undefined && (
            <Typography
              variant="body2"
              sx={{
                fontSize: 12,
                color: "#333",
                fontWeight: 700,
                minWidth: 35,
                textAlign: "right",
                flexShrink: 0,
              }}
            >
              {percentage}%
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * Labeled Bar Chart Component
 * Displays horizontal bars with labels above and values/percentages on the right
 * @param {Object} props
 * @param {Array} props.data - Array of data items
 * @param {boolean} props.enabled - Whether the chart should render
 * @param {string} [props.labelField="accountname"] - Field name for the label
 * @param {string} [props.valueField="entries"] - Field name for the value
 * @param {string} [props.thresholdField] - Field name for threshold (optional)
 * @param {boolean} [props.showPercentage=true] - Whether to show percentage
 * @param {number} [props.maxHeight=350] - Maximum height before scrolling
 * @param {Function} [props.tooltip] - Tooltip renderer: ({label, value, percentage, item}) => ReactNode
 * @returns {import("react").ReactElement}
 */
export default function LabeledBarChart(props) {
  const {
    data = [],
    enabled,
    labelField = "accountname",
    valueField = "entries",
    thresholdField,
    showPercentage = true,
    maxHeight = 350,
    tooltip,
  } = props;

  if (!enabled || !data || data.length === 0) {
    return <Box sx={{ width: "100%", minHeight: 200 }} />;
  }

  // Memoize computed chart data to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    // Filter out placeholder items and prepare data
    const validData = data.filter((item) => item[labelField] !== "remove");

    // Sort by value descending and take top items
    const sortedData = [...validData].sort((a, b) => (b[valueField] || 0) - (a[valueField] || 0));

    if (sortedData.length === 0) {
      return null;
    }

    // Calculate total for percentages
    const total = sortedData.reduce((sum, item) => sum + (item[valueField] || 0), 0);

    // Find max value for bar width calculation
    const maxValue = Math.max(...sortedData.map((item) => item[valueField] || 0), 0);

    // Generate colors
    const chartColors = generateChartColors(sortedData.length);

    return { sortedData, total, maxValue, chartColors };
  }, [data, labelField, valueField]);

  // Return empty state if no valid data after filtering
  if (!chartData) {
    return <Box sx={{ width: "100%", minHeight: 200 }} />;
  }

  const { sortedData, total, maxValue, chartColors } = chartData;

  return (
    <Box
      sx={{
        width: "100%",
        pt: 1,
        pr: 1,
        maxHeight,
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {sortedData.map((item, index) => {
        const value = item[valueField] || 0;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        const widthPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
        const threshold = thresholdField ? item[thresholdField] || 0 : 0;
        const thresholdPercent = threshold > 0 && maxValue > 0 ? (threshold / maxValue) * 100 : 0;

        return (
          <BarItem
            key={item[labelField] || index}
            label={item[labelField] || "Unknown"}
            value={value}
            percentage={percentage}
            showPercentage={showPercentage}
            widthPercent={widthPercent}
            color={chartColors[index]}
            item={item}
            threshold={threshold}
            thresholdPercent={thresholdPercent}
            tooltip={tooltip}
          />
        );
      })}
    </Box>
  );
}
